<template>
  <div
    :class="$style.deviceGroupAsidePanelContainer"
  >
    <AsidePanel
      ref="asidePanel"
      :components="components"
      :handleSave="handleSave"
      :isExpanded="isExpanded"
      :itemId="itemId"
      :itemType="itemType"
      :redirectTo="redirectTo"
      :viewport="viewport"
    >
      <template v-slot:avatar>
        <AsideIcon
          icon="deviceGroup"
        />
      </template>
      <template v-slot:leftPaneCustomContent>
        <DefaultDeviceGroupAside />
      </template>
    </AsidePanel>
    <SeverityModal
      ref="userGroupBindingChangeModal"
      notificationTitle="This will alter who can log in to your devices."
      :primaryButtonProps="primaryButtonProps"
      :showModal="showUserGroupBindingModal"
      :title="userGroupModalTitle"
      variant="sev2"
      @close="handleCancelUserGroupChange"
      @save="handleConfirmUserGroupChange"
    >
      <template v-slot:subtitle>
        <span>Are you sure you want to change the device group's assigned user groups?</span>
      </template>
      <template v-slot:body>
        <ParagraphBody>
          <div v-if="userGroupsAreBound">
            Adding a user group to the device group will create local accounts on <b>all</b>
            devices in the device groups. This may lead to performance issues on the devices.
          </div>
          <br v-if="userGroupsAreBound && userGroupsAreUnbound">
          <div v-if="userGroupsAreUnbound">
            Removing a user group from a device group will cause those users to lose
            <b>all</b> access to these devices.
          </div>
        </ParagraphBody>
      </template>
      <template v-slot:learnmore>
        <ParagraphBody>
          For more information, see
          <Link
            href="https://support.jumpcloud.com/s/article/Binding-Users-to-Devices"
            target="_blank"
            text="Binding Users to Devices"
          />
        </ParagraphBody>
      </template>
    </SeverityModal>
  </div>
</template>
<script>
import {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  Link,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import ConfigurationGroupItemTable from 'client/components/ItemTable/ConfigurationGroupItemTable.vue';
import DefaultDeviceGroupAside from 'client/groups/System/DeviceGroupAssociations.vue';
import FeatureTrialMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import ItemType from 'client/util/Constants/ItemType';
import PoliciesItemTable from 'client/components/ItemTable/PoliciesItemTable.vue';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemGroupDetails from 'client/groups/System/SystemGroupDetails.vue';
import SystemGroupVPN from 'client/groups/System/SystemGroupVPN.vue';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';
import modalUtil from 'client/AdminPortal/util/ModalUtil';

const { ConfigurationItemType, ConfigurationGroupItemType, DeviceGroupItemType } = ItemType;
const configurationGroupItem = new ConfigurationGroupItemType();
const configurationItem = new ConfigurationItemType();
const deviceGroupItem = new DeviceGroupItemType();

const { create, update } = ItemActionConsts;
const groupCollection = StoreModuleMap[ItemTypeConsts.systemGroup];
const systemGroupModel = StoreModelMap[ItemTypeConsts.systemGroup];
const policyCollection = 'PolicyCollection';
const { PrimaryButtonProps } = Types;

export default {
  name: 'SystemGroupAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    DefaultDeviceGroupAside,
    Link,
    ParagraphBody,
    SeverityModal,
  },

  mixins: [
    AsidePanelPropsMixin,
    FeatureTrialMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.deviceGroup,
      required: false,
    },
  },

  data() {
    const vpnComponent = this.$store.getters['FeatureFlagsModule/betaFeatures'].labsDeviceGroupVPN ? [{
      label: 'VPN',
      vueComponent: SystemGroupVPN,
      vueComponentProps: {},
    }] : [];

    return {
      itemHasBeenSaved: false,
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      redirectTo: ItemTypeRoutes[ItemTypeConsts.deviceGroup],
      userGroupsAreUnbound: false,
      userGroupsAreBound: false,
      showUserGroupBindingModal: false,
      components: [
        {
          label: 'Details',
          vueComponent: SystemGroupDetails,
        },
        {
          backboneView: null,
          iconClassName: 'jc-systems1',
          itemType: ItemTypeConsts.device,
          label: 'Devices',
          headerLabels: {
            [create]: 'The following devices are members of this device group',
            [update]: 'contains the following devices',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/systemgroups/${this.itemId}/membership`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        {
          backboneView: null,
          iconClassName: 'jc-groups-users',
          itemType: ItemTypeConsts.userGroup,
          label: 'User Groups',
          headerLabels: {
            [create]: `This ${deviceGroupItem.label} is bound to the following user groups`,
            [update]: 'is bound to the following user groups',
          },
          vueComponent: UserGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/systemgroups/${this.itemId}/usergroups`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        {
          backboneView: null,
          iconClassName: 'jc-policies1',
          itemType: ItemTypeConsts.policy,
          label: configurationItem.titlePlural,
          headerLabels: {
            [create]: `The following ${configurationItem.labelPlural} will be applied to this ${deviceGroupItem.label}`,
            [update]: `has the following ${configurationItem.labelPlural} applied`,
          },
          vueComponent: PoliciesItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/systemgroups/${this.itemId}/policies`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        {
          itemType: configurationGroupItem.type,
          label: configurationGroupItem.titlePlural,
          headerLabels: {
            [create]: `The following ${configurationGroupItem.labelPlural} will be applied to this ${deviceGroupItem.label}`,
            [update]: `has the following ${configurationGroupItem.labelPlural} applied`,
          },
          vueComponent: ConfigurationGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/systemgroups/${this.itemId}/policygroups`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        ...vpnComponent,
      ],
    };
  },

  computed: {
    ...mapGetters(systemGroupModel, [
      'getTargetsToBind',
    ]),

    ...mapGetters(policyCollection, {
      getPolicy: 'getItem',
    }),

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        hasOutline: false,
        text: this.userGroupModalButtonText,
        type: 'primary',
      });
    },
    userGroupModalButtonText() {
      let buttonText;
      if (this.userGroupsAreBound) {
        buttonText = this.userGroupsAreUnbound
          ? 'Confirm'
          : 'Bind User Groups';
      } else {
        buttonText = this.userGroupsAreUnbound
          ? 'Unbind User Groups'
          : 'Save';
      }
      return buttonText;
    },
    userGroupModalTitle() {
      let titleText;
      if (this.userGroupsAreBound) {
        titleText = this.userGroupsAreUnbound
          ? 'Change Assigned User Groups'
          : 'Bind User Groups to Device Groups';
      } else {
        titleText = this.userGroupsAreUnbound
          ? 'Unbind User Groups from Device Groups'
          : 'Change Assigned User Groups';
      }
      return titleText;
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasModelChanged()) {
      modalUtil.unSavedChangesModal(next);
    } else {
      next();
    }
  },

  methods: {
    ...mapActions(systemGroupModel, [
      'saveItem',
      'bindSystemGroupToConfigurationGroups',
      'bindSystemGroupToPolicies',
      'bindSystemGroupToSystems',
      'bindSystemGroupToUserGroups',
    ]),

    ...mapActions(groupCollection, [
      'refreshCollection',
    ]),

    ...mapGetters(systemGroupModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'getUserGroupAssociationCounts',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    handleCancelUserGroupChange() {
      this.showUserGroupBindingModal = false;
    },

    async handleConfirmUserGroupChange() {
      try {
        await this.saveAndBindDeviceGroup();
        this.triggerSuccessNotification({
          message: 'Device Group saved successfully!',
        });
        this.$refs.asidePanel.redirect();
      } catch (error) {
        let message = '**Unable to save Device Group!**';

        if (error && error.message && !error.details) {
          message = `${message}\n${error.message}`;
        }
        this.triggerErrorNotification({ isMarkdown: true, message });
      } finally {
        this.showUserGroupBindingModal = false;
      }
    },

    async handleSave() {
      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      const hasUserGroupsChanged = this.handleUserGroupAssociationChange();
      if (!hasUserGroupsChanged) {
        await this.saveAndBindDeviceGroup();
      }

      return new SaveReturnValue({
        canContinue: !hasUserGroupsChanged,
        noNotification: hasUserGroupsChanged,
      });
    },

    handleUserGroupAssociationChange() {
      const { bind, unbind } = this.getUserGroupAssociationCounts();
      this.showUserGroupBindingModal = (bind || unbind) !== 0;
      this.userGroupsAreBound = bind !== 0;
      this.userGroupsAreUnbound = unbind !== 0;
      return this.showUserGroupBindingModal;
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    async saveAndBindDeviceGroup() {
      try {
        this.itemHasBeenSaved = true;
        const item = await this.saveItem();
        await this.bindSystemGroupToSystems();
        await this.bindSystemGroupToUserGroups();
        await this.bindSystemGroupToPolicies();
        await this.bindSystemGroupToConfigurationGroups();
        await this.refreshCollection({ item });
        this.refreshTrialData(trialPropsData.osPatchManagement.featureCode);
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        let message = '**Unable to save Device Group!**';

        if (error && error.message && !error.details) {
          message = `${message}\n${error.message}`;
        }
        throw Error(message);
      }
    },
  },
};
</script>

<style module>
.deviceGroupAsidePanelContainer {
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 20;
}

</style>
